<template>
  <Teleport to="body">
    <Transition name="mall-login-modal">
      <div
        v-if="open"
        class="mall-login-modal"
        role="presentation"
        @mousedown.self="emit('close')"
      >
        <section
          ref="panelRef"
          class="mall-login-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gate-login-title"
        >
          <button
            ref="closeRef"
            type="button"
            class="mall-login-modal__close"
            aria-label="로그인 창 닫기"
            @click="emit('close')"
          >
            <AppIcon icon="mdi-close" :size="22" />
          </button>

          <p class="mall-login-modal__eyebrow">HAMWOORI MALL</p>
          <GateLoginPanel
            context="mall"
            :redirect-to="redirectTo"
            @success="emit('success')"
          />
          <p class="mall-login-modal__note">
            장기요양지원센터
          </p>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import GateLoginPanel from '@/components/auth/GateLoginPanel.vue'

const props = defineProps<{
  open: boolean
  redirectTo: string
}>()

const emit = defineEmits<{
  close: []
  success: []
}>()

const panelRef = ref<HTMLElement | null>(null)
const closeRef = ref<HTMLButtonElement | null>(null)
let previousOverflow = ''
let scrollLocked = false

function onKeydown(event: KeyboardEvent) {
  if (!props.open) return
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
    return
  }
  if (event.key !== 'Tab' || !panelRef.value) return

  const focusable = Array.from(
    panelRef.value.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), [tabindex]:not([tabindex="-1"])',
    ),
  )
  if (!focusable.length) return

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      previousOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      scrollLocked = true
      document.addEventListener('keydown', onKeydown)
      await nextTick()
      closeRef.value?.focus()
      return
    }
    if (scrollLocked) {
      document.body.style.overflow = previousOverflow
      scrollLocked = false
    }
    document.removeEventListener('keydown', onKeydown)
  },
)

onBeforeUnmount(() => {
  if (scrollLocked) document.body.style.overflow = previousOverflow
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style lang="scss" src="./MallLoginModal.scss"></style>
